import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import ElectricalServicesOutlinedIcon from '@mui/icons-material/ElectricalServicesOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import RouterOutlinedIcon from '@mui/icons-material/RouterOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import FactoryOutlinedIcon from '@mui/icons-material/FactoryOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';

export const Home = () => {
  const { t } = useTranslation();

  const serviceCards = [
    { key: 'installation', icon: ElectricalServicesOutlinedIcon },
    { key: 'lighting', icon: LightbulbOutlinedIcon },
    { key: 'safety', icon: SecurityOutlinedIcon },
    { key: 'network', icon: RouterOutlinedIcon }
  ];

  const sectorCards = [
    { key: 'private', icon: HomeOutlinedIcon },
    { key: 'business', icon: BusinessOutlinedIcon },
    { key: 'industry', icon: FactoryOutlinedIcon }
  ];

  const valueCards = [
    { key: 'planning', icon: VerifiedOutlinedIcon },
    { key: 'execution', icon: HandshakeOutlinedIcon },
    { key: 'service', icon: SupportAgentOutlinedIcon }
  ];

  return (
    <>
      <section className="hero" aria-labelledby="hero-title">
        <div className="container hero__content">
          <div className="hero__text">
            <span className="hero__eyebrow">{t('hero.eyebrow')}</span>
            <h1 id="hero-title" className="hero__headline">
              {t('hero.headline')}
            </h1>
            <p className="hero__lead">{t('hero.lead')}</p>
            <div className="hero__actions">
              <Link className="btn btn--primary" to="/kontakt">
                {t('hero.primaryCta')}
              </Link>
            </div>
          </div>
          <div className="hero__panel">
            <div className="hero__badge">
              <span className="hero__badge-icon" role="img" aria-hidden="true">
                ⚡
              </span>
              <div>
                <p className="hero__badge-title">{t('hero.badge.title')}</p>
                <p className="hero__badge-text">{t('hero.badge.text')}</p>
              </div>
            </div>
            <ul className="hero__list">
              <li>
                <span className="hero__list-number">{t('hero.stats.experience.number')}</span>
                <span className="hero__list-label">{t('hero.stats.experience.label')}</span>
              </li>
              <li>
                <span className="hero__list-number">{t('hero.stats.coverage.number')}</span>
                <span className="hero__list-label">{t('hero.stats.coverage.label')}</span>
              </li>
              <li>
                <span className="hero__list-number">{t('hero.stats.response.number')}</span>
                <span className="hero__list-label">{t('hero.stats.response.label')}</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <section className="section section--light">
        <div className="container">
          <header className="section__header">
            <span className="section__eyebrow">{t('home.services.eyebrow')}</span>
            <h2>{t('home.services.title')}</h2>
            <p className="section__lead">{t('home.services.lead')}</p>
          </header>
          <div className="home-services__grid">
            {serviceCards.map(({ key, icon: Icon }) => (
              <article className="home-service-card" key={key}>
                <div className="home-service-card__header">
                  <span className="home-service-card__icon" aria-hidden="true">
                    <Icon fontSize="small" />
                  </span>
                  <span className="home-service-card__label">{t(`home.services.items.${key}.label`)}</span>
                </div>
                <h3 className="home-service-card__title">{t(`home.services.items.${key}.title`)}</h3>
                <p className="home-service-card__text">{t(`home.services.items.${key}.text`)}</p>
                <ul className="home-service-card__list">
                  <li>{t(`home.services.items.${key}.points.one`)}</li>
                  <li>{t(`home.services.items.${key}.points.two`)}</li>
                  <li>{t(`home.services.items.${key}.points.three`)}</li>
                </ul>
                <Link className="home-service-card__link" to={`/leistungen/${key === 'installation' ? 'elektroinstallation' :
                    key === 'lighting' ? 'beleuchtung' :
                      key === 'safety' ? 'sprechanlagen' :
                        'netzwerktechnik'
                  }`}>
                  {t('home.services.action')}
                  <span aria-hidden="true">→</span>
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <header className="section__header">
            <span className="section__eyebrow">{t('home.sectors.eyebrow')}</span>
            <h2>{t('home.sectors.title')}</h2>
            <p className="section__lead">{t('home.sectors.lead')}</p>
          </header>
          <div className="home-sectors__grid">
            {sectorCards.map(({ key, icon: Icon }) => (
              <article className="home-sector-card" key={key}>
                <span className="home-sector-card__icon" aria-hidden="true">
                  <Icon fontSize="small" />
                </span>
                <h3 className="home-sector-card__title">{t(`home.sectors.items.${key}.title`)}</h3>
                <p className="home-sector-card__text">{t(`home.sectors.items.${key}.text`)}</p>
                <div className="home-sector-card__tags">
                  <span className="home-sector-card__tag">{t(`home.sectors.items.${key}.points.one`)}</span>
                  <span className="home-sector-card__tag">{t(`home.sectors.items.${key}.points.two`)}</span>
                  <span className="home-sector-card__tag">{t(`home.sectors.items.${key}.points.three`)}</span>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <header className="section__header">
            <span className="section__eyebrow">{t('home.values.eyebrow')}</span>
            <h2>{t('home.values.title')}</h2>
            <p className="section__lead">{t('home.values.lead')}</p>
          </header>
          <div className="home-values__grid">
            {valueCards.map(({ key, icon: Icon }) => (
              <article className="value-card" key={key}>
                <span className="value-card__icon" aria-hidden="true">
                  <Icon fontSize="small" />
                </span>
                <h3 className="value-card__title">{t(`home.values.cards.${key}.title`)}</h3>
                <p className="value-card__text">{t(`home.values.cards.${key}.text`)}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section section--accent">
        <div className="container">
          <header className="section__header">
            <span className="section__eyebrow section__eyebrow--on-dark">{t('home.process.eyebrow')}</span>
            <h2>{t('home.process.title')}</h2>
            <p className="section__lead section__lead--on-dark">{t('home.process.lead')}</p>
          </header>
          <div className="process-steps">
            <article className="process-step">
              <span className="process-step__index">01</span>
              <h3 className="process-step__title">{t('home.process.steps.consult.title')}</h3>
              <p className="process-step__text">{t('home.process.steps.consult.text')}</p>
            </article>
            <article className="process-step">
              <span className="process-step__index">02</span>
              <h3 className="process-step__title">{t('home.process.steps.plan.title')}</h3>
              <p className="process-step__text">{t('home.process.steps.plan.text')}</p>
            </article>
            <article className="process-step">
              <span className="process-step__index">03</span>
              <h3 className="process-step__title">{t('home.process.steps.deliver.title')}</h3>
              <p className="process-step__text">{t('home.process.steps.deliver.text')}</p>
            </article>
          </div>
        </div>
      </section>

      <section className="section section--light">
        <div className="container home-secondary-cta">
          <div>
            <h2>{t('home.secondary.title')}</h2>
            <p className="section__lead">{t('home.secondary.lead')}</p>
          </div>
          <Link className="btn btn--primary" to="/unternehmen">
            {t('home.secondary.button')}
          </Link>
        </div>
      </section>
    </>
  );
};
